package dev.lingvo.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import dev.lingvo.shared.UserDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Session
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LMailIcon
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch

/** Verification enables platform-funded translation; ordinary work and BYOK remain available. */
@Composable
internal fun verifyEmailNotice(
    account: UserDto? = Session.user,
    resend: suspend () -> Unit = { ApiClient.resendVerification() },
): ShellNotice? {
    val user = account ?: return null
    if (user.emailVerifiedAt != null) return null

    val scope = rememberCoroutineScope()
    var sending by remember(user.id, user.email) { mutableStateOf(false) }
    var sent by remember(user.id, user.email) { mutableStateOf(false) }
    var failure by remember(user.id, user.email) { mutableStateOf<String?>(null) }

    return ShellNotice(
        id = "verify-email",
        // `{email}` rather than a Kotlin template. This file had no catalogue lookup in it at all,
        // so every sentence an unverified account ever reads — the ask, the confirmation and the
        // failure — was English whatever language the rest of the shell was in.
        title = when {
            failure != null -> t(
                "verify_email_banner.that_email_didnt_go_out",
                "That email didn't go out",
            )

            sent -> tf(
                "verify_email_banner.verification_sent_to_email",
                "Verification requested for {email}.",
                "email" to user.email,
            )

            else -> tf(
                "verify_email_banner.confirm_email",
                "Confirm {email}.",
                "email" to user.email,
            )
        },
        description = when {
            failure != null -> failure
            sent -> t(
                "verify_email_banner.check_your_inbox_the_link_expires",
                "Check your inbox and spam folder. The link expires after three days. If it does not arrive, you can request another.",
            )

            else -> t(
                "verify_email_banner.so_you_can_recover_this_account",
                "Confirm your address to use Lingvo-funded translation and recover your account. Your own provider key, manual work, import, export and QA remain available.",
            )
        },
        variant = if (failure != null) LAlertVariant.WARNING else LAlertVariant.INFO,
        action = {
            LButton(
                label = if (failure != null) {
                    t("verify_email_banner.try_again", "Try again")
                } else if (sent) {
                    t("verify_email_banner.send_another", "Send another link")
                } else {
                    t("verify_email_banner.resend_verification", "Resend verification")
                },
                onClick = onClick@{
                    if (sending) return@onClick
                    scope.launch {
                        sending = true
                        failure = null
                        try {
                            resend()
                            sent = true
                        } catch (cancelled: CancellationException) {
                            throw cancelled
                        } catch (error: ApiException) {
                            // **Still never `error.message`, though the reason has changed.**
                            // `ApiTransport` used to fall back to the raw response body, so
                            // binding it to a pixel put the mail provider's reply on a
                            // customer's screen; it now collapses anything unstructured to a
                            // sentence of ours (`ApiTransport.toApiException`). What is left is
                            // the reason this branch is here at all: there is nothing to branch
                            // on. Every way "send it again" can fail leaves the reader the same
                            // next action, and a delivery error names a system they cannot
                            // reach.
                            failure = resendFailed()
                        } catch (error: Throwable) {
                            failure = resendFailed()
                        } finally {
                            sending = false
                        }
                    }
                },
                variant = LButtonVariant.SECONDARY,
                size = LButtonSize.SM,
                enabled = !sending,
                loading = sending,
                leadingIcon = { LMailIcon() },
            )
        },
    )
}

/**
 * The one thing a failed resend says, whatever went wrong.
 *
 * A fixed, client-owned sentence: it names what failed, implies the account is unharmed, and gives
 * the only action that helps. It carries no detail from the response, because there is no detail in
 * a mail-delivery failure that a customer can act on.
 *
 * A function reading `tr` rather than a `const`, so it can be translated: it is read inside a
 * `catch` in a coroutine, which is not composition, and a `const` cannot be a catalogue lookup.
 */
private fun resendFailed(): String = tr(
    "verify_email_banner.we_couldnt_send_that_email_just",
    "We couldn't send that email just now. Try again in a minute.",
)
